import React, { useState } from 'react';
import axios from 'axios';
import { message } from 'antd';
import dayjs from 'dayjs';

const SearchFunctions = ({ 
  onSearchResults,
  userBatchesForSearch,
  setUserBatchesForSearch,
  airwayBillId,
  children,
  orders = []
}) => {
  // Pagination state for search results
  const [searchPagination, setSearchPagination] = useState(null);
  // Modal states
  const [searchModalVisible, setSearchModalVisible] = useState(false);
  const [expressSearchModalVisible, setExpressSearchModalVisible] = useState(false);
  const [batchNumberSearchModalVisible, setBatchNumberSearchModalVisible] = useState(false);
  const [dateRangeModalVisible, setDateRangeModalVisible] = useState(false);
  const [recipientSearchModalVisible, setRecipientSearchModalVisible] = useState(false);
  const [recipientPhoneSearchModalVisible, setRecipientPhoneSearchModalVisible] = useState(false);
  const [senderSearchModalVisible, setSenderSearchModalVisible] = useState(false);
  const [senderPhoneSearchModalVisible, setSenderPhoneSearchModalVisible] = useState(false);
  const [statusFilterModalVisible, setStatusFilterModalVisible] = useState(false);
  const [customerSearchModalVisible, setCustomerSearchModalVisible] = useState(false);

  // Form states
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [searchOrderNumbers, setSearchOrderNumbers] = useState('');
  const [searchExpressNumbers, setSearchExpressNumbers] = useState('');
  const [searchBatchNumber, setSearchBatchNumber] = useState('');
  const [dateRange, setDateRange] = useState(null);
  const [searchRecipientName, setSearchRecipientName] = useState('');
  const [searchRecipientPhone, setSearchRecipientPhone] = useState('');
  const [searchSenderName, setSearchSenderName] = useState('');
  const [searchSenderPhone, setSearchSenderPhone] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(null);

  const fetchUserBatchesForSearch = async () => {
    try {
      const response = await axios.get('/api/orders/user_batches/');
      setUserBatchesForSearch(response.data || []);
    } catch (error) {
      console.error('Error fetching user batches:', error);
      message.error('获取用户批次数据失败');
    }
  };

  const handleShowBatchNumberSearch = async () => {
    await fetchUserBatchesForSearch();
    setBatchNumberSearchModalVisible(true);
  };

  // Modal search handlers
  const handleModalSearch = async () => {
    if (!searchOrderNumbers.trim()) {
      message.warning('请输入要搜索的订单号');
      return;
    }

    const orderNumbers = searchOrderNumbers
      .split(/[\n,，]/)
      .map(num => num.trim())
      .filter(num => num);

    if (orderNumbers.length === 0) {
      message.warning('请输入有效的订单号');
      return;
    }

    try {
      // Create a new array to avoid any potential circular references
      const cleanOrderNumbers = [...orderNumbers];
      const response = await axios.post('/api/orders/search/', {
        order_numbers: cleanOrderNumbers
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
        setSearchModalVisible(false);
        setSearchOrderNumbers('');
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      message.error('搜索订单失败');
    }
  };

  // Direct search handlers for toolbar
  const handleDirectSearch = async (value) => {
    if (!value) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const orderNumbers = value.split(',').map(num => num.trim()).filter(num => num);
      // Create a new array to avoid any potential circular references
      const cleanOrderNumbers = [...orderNumbers];
      const response = await axios.post('/api/orders/search/', {
        order_numbers: cleanOrderNumbers
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching orders:', error);
      message.error('搜索订单失败');
    }
  };

  const handleDirectExpressSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const expressNumbers = [searchValue];
      const response = await axios.post('/api/orders/search_express/', {
        express_numbers: expressNumbers
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching express numbers:', error);
      message.error('搜索快递单号失败');
    }
  };

  const handleDirectBatchSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const batchNumbers = [searchValue];
      const response = await axios.post('/api/orders/search_batch/', {
        batch_numbers: batchNumbers
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching batch number:', error);
      message.error('搜索批次号失败');
    }
  };

  const handleDirectRecipientSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const recipientNames = [searchValue];
      const response = await axios.post('/api/orders/search_recipient/', {
        recipient_names: recipientNames
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching recipients:', error);
      message.error('搜索收件人失败');
    }
  };

  const handleDirectRecipientPhoneSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const recipientPhones = [searchValue];
      const response = await axios.post('/api/orders/search_recipient_phone/', {
        recipient_phones: recipientPhones
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching recipient phones:', error);
      message.error('搜索收件人电话失败');
    }
  };

  const handleDirectSenderSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const senderNames = [searchValue];
      const response = await axios.post('/api/orders/search_sender/', {
        sender_names: senderNames
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching senders:', error);
      message.error('搜索寄件人失败');
    }
  };

  // Current search state to handle pagination
  const [currentSearchType, setCurrentSearchType] = useState(null);
  const [currentSearchValue, setCurrentSearchValue] = useState(null);
  
  // Handle page change for search results
  const handleSearchPageChange = (page) => {
    console.log('handleSearchPageChange called with page', page);
    console.log('currentSearchType:', currentSearchType, 'currentSearchValue:', currentSearchValue);
    console.log('searchPagination:', searchPagination);
    
    if (currentSearchType === 'status') {
      console.log('Calling handleDirectStatusSearch with page', page);
      handleDirectStatusSearch(currentSearchValue, page, searchPagination?.page_size || 500);
    } else if (currentSearchType === 'bool_field') {
      console.log('Calling handleBoolFieldSearch with page', page);
      handleBoolFieldSearch(currentSearchValue.field, currentSearchValue.value, page, searchPagination?.page_size || 500);
    } else {
      console.log('No currentSearchType set, cannot change page');
      // If we don't have a currentSearchType, let's try to handle a hardcoded page 2 request
      if (page === 2) {
        // Hardcoded search for page 2 with 500 items per page
        console.log('Hardcoded search for page 2');
        const field = 'is_inspection_needed';
        const value = true;
        handleBoolFieldSearch(field, value, 2, 500);
      }
    }
  };
  
  // Handle page size change for search results
  const handleSearchPageSizeChange = (pageSize) => {
    // Update the pagination state immediately
    setSearchPagination(prev => ({
      ...prev,
      page_size: pageSize,
      page: 1 // Reset to page 1 when changing page size
    }));
    
    if (currentSearchType === 'status') {
      handleDirectStatusSearch(currentSearchValue, 1, pageSize);
    } else if (currentSearchType === 'bool_field') {
      handleBoolFieldSearch(currentSearchValue.field, currentSearchValue.value, 1, pageSize);
    }
  };

  // Generic handler for boolean field searches
  const handleBoolFieldSearch = async (field, value, page = 1, pageSize = 500) => {
    // Save current search type and value for pagination
    setCurrentSearchType('bool_field');
    setCurrentSearchValue({ field, value });
    try {
      const response = await axios.post('/api/orders/search_bool_field/', {
        field,
        value,
        airway_bill_id: airwayBillId,
        page,
        page_size: pageSize
      });
      
      // Check if the response has the new paginated format
      if (response.data && response.data.results) {
        const { results, count, page: responsePage, page_size, total_pages } = response.data;
        
        // Update pagination state
        const newPagination = {
          count,
          page: responsePage,
          page_size,
          total_pages
        };
        console.log('Setting search pagination:', newPagination);
        setSearchPagination(newPagination);
        
        if (results.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${count} 个订单，显示第 ${page} 页`);
          // Pass results, pagination info, and search params to the callback
          onSearchResults(results, {
            count,
            page: responsePage,
            page_size,
            total_pages
          }, {
            type: 'bool_field',
            field,
            value,
            status: null
          });
        }
      } else {
        // Fallback for old format
        if (response.data.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${response.data.length} 个订单`);
          onSearchResults(response.data);
        }
      }
    } catch (error) {
      console.error(`Error searching by ${field}:`, error);
      message.error('筛选失败');
    }
  };

  const handleDirectStatusSearch = async (value, page = 1, pageSize = 500) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Save current search type and value for pagination
      setCurrentSearchType('status');
      setCurrentSearchValue(searchValue);
      
      const response = await axios.post('/api/orders/search_status/', {
        status: searchValue,
        airway_bill_id: airwayBillId,
        page: page,
        page_size: pageSize
      });
      
      // Check if the response has the new paginated format
      if (response.data && response.data.results) {
        const { results, count, page: responsePage, page_size, total_pages } = response.data;
        
        // Update pagination state
        const newPagination = {
          count,
          page: responsePage,
          page_size,
          total_pages
        };
        console.log('Setting search pagination for status search:', newPagination);
        setSearchPagination(newPagination);
        
        if (results.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${count} 个订单，显示第 ${page} 页`);
          // Pass results, pagination info, and search params to the callback
          console.log('Passing to onSearchResults - count:', count);
          console.log('Passing to onSearchResults - results.length:', results.length);
          onSearchResults(results, {
            count,
            page: responsePage,
            page_size,
            total_pages
          }, {
            type: 'status',
            field: null,
            value: null,
            status: searchValue
          });
        }
      } else {
        // Fallback for old format
        if (response.data.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${response.data.length} 个订单`);
          onSearchResults(response.data);
        }
      }
    } catch (error) {
      console.error('Error searching by status:', error);
      message.error('状态筛选失败');
    }
  };

  const handleDirectSenderPhoneSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      // Create a simple array with just the value to avoid circular references
      const senderPhones = [searchValue];
      const response = await axios.post('/api/orders/search_sender_phone/', {
        sender_phones: senderPhones
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching sender phones:', error);
      message.error('搜索寄件人电话失败');
    }
  };

  const handleDirectPrintCountSearch = async (value) => {
    // Handle both string values and event objects
    // If value is an event object or has a target property, extract the value
    const searchValue = value && value.target ? value.target.value : value;
    
    if (!searchValue) return;

    try {
      const response = await axios.post('/api/orders/search_print_count/', {
        print_count: parseInt(searchValue),
        airway_bill_id: airwayBillId
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching by print count:', error);
      message.error('搜索打印次数失败');
    }
  };

  const handleDirectDateFilterSearch = async (dates) => {
    if (!dates || dates.length === 0) return;

    try {
      const response = await axios.post('/api/orders/search_dates/', {
        dates: dates,
        airway_bill_id: airwayBillId
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
      }
    } catch (error) {
      console.error('Error searching by dates:', error);
      message.error('日期筛选失败');
    }
  };

  const handleDateRangeSearch = async () => {
    if (!dateRange || !dateRange[0] || !dateRange[1]) {
      message.warning('请选择日期范围');
      return;
    }

    try {
      const startDate = dayjs(dateRange[0]).format('YYYY-MM-DD 00:00:00');
      const endDate = dayjs(dateRange[1]).format('YYYY-MM-DD 23:59:59');

      const response = await axios.post('/api/orders/search_date/', {
        start_date: startDate,
        end_date: endDate
      });
      if (response.data.length === 0) {
        message.info('未找到匹配的订单');
      } else {
        message.success(`找到 ${response.data.length} 个订单`);
        onSearchResults(response.data);
        setDateRangeModalVisible(false);
        setDateRange(null);
      }
    } catch (error) {
      console.error('Error searching by date range:', error);
      message.error('搜索日期范围失败');
    }
  };

  const handleStatusFilter = async (airwayBillId = null, page = 1, pageSize = 500) => {
    if (!selectedStatus) {
      message.warning('请选择状态');
      return;
    }

    const hide = message.loading('正在筛选...', 0);
    try {
      const response = await axios.post('/api/orders/search_status/', {
        status: selectedStatus,
        airway_bill_id: airwayBillId,
        page: page,
        page_size: pageSize
      });
      hide();
      
      // Check if the response has the new paginated format
      if (response.data && response.data.results) {
        const { results, count, page: responsePage, page_size, total_pages } = response.data;
        if (results.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${count} 个订单，显示第 ${page} 页`);
          // Pass results, pagination info, and search params to the callback
          console.log('handleStatusFilter - Passing to onSearchResults - count:', count);
          console.log('handleStatusFilter - Passing to onSearchResults - results.length:', results.length);
          
          // Create a custom event to pass the count directly to OrderList
          const countEvent = new CustomEvent('statusFilterCount', { 
            detail: { count } 
          });
          window.dispatchEvent(countEvent);
          
          onSearchResults(results, {
            count,
            page: responsePage,
            page_size,
            total_pages
          }, {
            type: 'status',
            field: null,
            value: null,
            status: selectedStatus
          });
        }
      } else {
        // Fallback for old format
        if (response.data.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${response.data.length} 个订单`);
          onSearchResults(response.data);
        }
      }
      
      setStatusFilterModalVisible(false);
      setSelectedStatus(null);
    } catch (error) {
      hide();
      console.error('Error filtering by status:', error);
      message.error('状态筛选失败');
    }
  };

  const props = {
    searchModalVisible,
    searchOrderNumbers,
    expressSearchModalVisible,
    searchExpressNumbers,
    batchNumberSearchModalVisible,
    searchBatchNumber,
    dateRangeModalVisible,
    dateRange,
    recipientSearchModalVisible,
    searchRecipientName,
    recipientPhoneSearchModalVisible,
    searchRecipientPhone,
    senderSearchModalVisible,
    searchSenderName,
    senderPhoneSearchModalVisible,
    searchSenderPhone,
    statusFilterModalVisible,
    selectedStatus,
    customerSearchModalVisible,
    customers,
    loadingCustomers,
    selectedCustomer,
    userBatchesForSearch,
    // Pagination state and handlers
    searchPagination,
    onSearchPageChange: handleSearchPageChange,
    onSearchPageSizeChange: handleSearchPageSizeChange,
    // Modal handlers for header icons
    onSearchModalCancel: () => {
      setSearchModalVisible(false);
      setSearchOrderNumbers('');
    },
    onSearchModalOk: handleModalSearch,
    onSearchOrderNumbersChange: e => setSearchOrderNumbers(e.target.value),
    onExpressSearchModalCancel: () => {
      setExpressSearchModalVisible(false);
      setSearchExpressNumbers('');
    },
    onExpressSearchModalOk: () => {
      if (searchExpressNumbers) {
        handleDirectExpressSearch(searchExpressNumbers);
      }
      setExpressSearchModalVisible(false);
    },
    onSearchExpressNumbersChange: e => setSearchExpressNumbers(e.target.value),
    onBatchNumberSearchModalCancel: () => {
      setBatchNumberSearchModalVisible(false);
      setSearchBatchNumber('');
    },
    onBatchNumberSearchModalOk: () => {
      if (searchBatchNumber) {
        handleDirectBatchSearch(searchBatchNumber);
      }
      setBatchNumberSearchModalVisible(false);
    },
    onSearchBatchNumberChange: setSearchBatchNumber,
    onDateRangeModalCancel: () => {
      setDateRangeModalVisible(false);
      setDateRange(null);
    },
    onDateRangeModalOk: handleDateRangeSearch,
    onDateRangeChange: setDateRange,
    onRecipientSearchModalCancel: () => {
      setRecipientSearchModalVisible(false);
      setSearchRecipientName('');
    },
    onRecipientSearchModalOk: () => {
      if (searchRecipientName) {
        handleDirectRecipientSearch(searchRecipientName);
      }
      setRecipientSearchModalVisible(false);
    },
    onSearchRecipientNameChange: e => setSearchRecipientName(e.target.value),
    onRecipientPhoneSearchModalCancel: () => {
      setRecipientPhoneSearchModalVisible(false);
      setSearchRecipientPhone('');
    },
    onRecipientPhoneSearchModalOk: () => {
      if (searchRecipientPhone) {
        handleDirectRecipientPhoneSearch(searchRecipientPhone);
      }
      setRecipientPhoneSearchModalVisible(false);
    },
    onSearchRecipientPhoneChange: e => setSearchRecipientPhone(e.target.value),
    onSenderSearchModalCancel: () => {
      setSenderSearchModalVisible(false);
      setSearchSenderName('');
    },
    onSenderSearchModalOk: () => {
      if (searchSenderName) {
        handleDirectSenderSearch(searchSenderName);
      }
      setSenderSearchModalVisible(false);
    },
    onSearchSenderNameChange: e => setSearchSenderName(e.target.value),
    onSenderPhoneSearchModalCancel: () => {
      setSenderPhoneSearchModalVisible(false);
      setSearchSenderPhone('');
    },
    onSenderPhoneSearchModalOk: () => {
      if (searchSenderPhone) {
        handleDirectSenderPhoneSearch(searchSenderPhone);
      }
      setSenderPhoneSearchModalVisible(false);
    },
    onSearchSenderPhoneChange: e => setSearchSenderPhone(e.target.value),
    onStatusFilterModalCancel: () => {
      setStatusFilterModalVisible(false);
      setSelectedStatus(null);
    },
    onStatusFilterModalOk: () => handleStatusFilter(airwayBillId),
    onStatusSelect: setSelectedStatus,
    onCustomerSearchModalCancel: () => {
      setCustomerSearchModalVisible(false);
    },
    onCustomerSearchModalOk: async () => {
      if (!selectedCustomer) {
        message.warning('请选择客户');
        return;
      }
      
      try {
        const response = await axios.post('/api/orders/search_customer/', {
          customer_id: selectedCustomer
        });
        
        if (response.data.length === 0) {
          message.info('未找到匹配的订单');
        } else {
          message.success(`找到 ${response.data.length} 个订单`);
          onSearchResults(response.data);
        }
        setCustomerSearchModalVisible(false);
        setSelectedCustomer(null);
      } catch (error) {
        console.error('Error searching by customer:', error);
        message.error('搜索客户失败');
      }
    },
    onCustomerSelect: (customerId) => {
      setSelectedCustomer(customerId);
    },
    // Header icon click handlers (show modals)
    onSearchOrder: () => setSearchModalVisible(true),
    onSearchExpress: () => setExpressSearchModalVisible(true),
    onSearchBatch: handleShowBatchNumberSearch,
    onSearchDate: () => setDateRangeModalVisible(true),
    onSearchRecipient: () => setRecipientSearchModalVisible(true),
    onSearchRecipientPhone: () => setRecipientPhoneSearchModalVisible(true),
    onSearchSender: () => setSenderSearchModalVisible(true),
    onSearchSenderPhone: () => setSenderPhoneSearchModalVisible(true),
    onSearchStatus: () => setStatusFilterModalVisible(true),
    onSearchCustomer: async () => {
      setCustomerSearchModalVisible(true);
      setLoadingCustomers(true);
      try {
        const response = await axios.get('/api/users/customers/');
        setCustomers(response.data || []);
      } catch (error) {
        console.error('Error fetching customers:', error);
        message.error('获取客户数据失败');
      } finally {
        setLoadingCustomers(false);
      }
    },
    // Direct search handlers for toolbar
    onSearchOrderDirect: handleDirectSearch,
    onSearchExpressDirect: handleDirectExpressSearch,
    onSearchBatchDirect: handleDirectBatchSearch,
    onSearchRecipientDirect: handleDirectRecipientSearch,
    onSearchRecipientPhoneDirect: handleDirectRecipientPhoneSearch,
    onSearchSenderDirect: handleDirectSenderSearch,
    onSearchSenderPhoneDirect: handleDirectSenderPhoneSearch,
    onSearchStatusDirect: handleDirectStatusSearch,
    onSearchPrintCountDirect: handleDirectPrintCountSearch,
    onSearchDateFilterDirect: handleDirectDateFilterSearch,
    // Boolean field search handlers
    onSearchInspection: (field, value) => handleBoolFieldSearch(field, value),
    onSearchShortPackage: (field, value) => handleBoolFieldSearch(field, value),
    onSearchClaim: (field, value) => handleBoolFieldSearch(field, value),
    onSearchModified: (field, value) => handleBoolFieldSearch(field, value),
  };

  return typeof children === 'function' 
    ? children(props)
    : React.cloneElement(children, props);
};

export default SearchFunctions;
